import type {
  ViewportTrackerOptions,
  ElementInfo,
  ViewportEventDetail,
} from '$js/isolated/ViewportTrackerService.d';

export class ViewportTrackerService {
  private options: Required<Omit<ViewportTrackerOptions, 'eventNameIn' | 'eventNameOut'>> & {
    eventNameIn: string;
    eventNameOut: string;
  };

  private elements: Map<Element, ElementInfo>;
  private seenElements: Set<Element>;
  private observer: IntersectionObserver;

  constructor(options: ViewportTrackerOptions = {}) {
    this.options = {
      threshold: options.threshold ?? 0.1,
      rootMargin: options.rootMargin ?? '0px',
      root: options.root ?? null,
      triggerOnce: options.triggerOnce ?? false,
      eventNameIn: options.eventNameIn ?? 'viewportVisible',
      eventNameOut: options.eventNameOut ?? 'viewportHidden',
    };

    this.elements = new Map<Element, ElementInfo>();
    this.seenElements = new Set<Element>();

    this.observer = new IntersectionObserver(this._handleIntersection.bind(this), {
      threshold: this.options.threshold,
      rootMargin: this.options.rootMargin,
      root: this.options.root,
    });
  }

  public track(element: Element | null): this {
    if (!element || !(element instanceof Element)) {
      return this;
    }

    this.elements.set(element, {
      visible: false,
    });

    this.observer.observe(element);

    return this;
  }

  public untrack(element: Element | null): this {
    if (!element) {
      return this;
    }

    this.elements.delete(element);
    this.observer.unobserve(element);

    return this;
  }

  public untrackAll(): this {
    this.elements.forEach((_, element) => {
      this.observer.unobserve(element);
    });

    this.elements.clear();
    this.seenElements.clear();

    return this;
  }

  public destroy(): void {
    this.observer.disconnect();
    this.elements.clear();
    this.seenElements.clear();
  }

  private _handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach((entry) => {
      const element = entry.target;
      const info = this.elements.get(element);

      if (!info) {
        return;
      }

      const wasVisible = info.visible;
      const isVisible = entry.isIntersecting;

      info.visible = isVisible;

      if (isVisible && !wasVisible) {
        if (!this.options.triggerOnce || !this.seenElements.has(element)) {
          this._triggerEvent(element, true);

          if (this.options.triggerOnce) {
            this.seenElements.add(element);
            this.observer.unobserve(element);
          }
        }
      } else if (!isVisible && wasVisible) {
        if (!this.options.triggerOnce || !this.seenElements.has(element)) {
          this._triggerEvent(element, false);
        }
      }
    });
  }

  private _triggerEvent(element: Element, isVisible: boolean): void {
    const detail: ViewportEventDetail = {
      element,
      time: Date.now(),
      tracker: this,
      isVisible,
    };

    const event = new CustomEvent<ViewportEventDetail>(
      isVisible ? this.options.eventNameIn : this.options.eventNameOut,
      {
        bubbles: true,
        detail,
      },
    );

    element.dispatchEvent(event);
  }
}
